/**
 * Service для работы с localStorage
 * Предоставляет типобезопасное API для хранения и получения данных
 */

const STORAGE_PREFIX = 'vinprop_'

export enum StorageKey {
  FEATURED_COMPLEXES = 'featured_complexes',
  FEATURED_ARTICLES = 'featured_articles',
  ARTICLES = 'articles',
  ARTICLE_SECTION_COUNTS = 'article_section_counts',
  CITIES = 'cities',
  COUNTRIES = 'countries',
  FILTER_VALUES = 'filter_values',
  LOCALE = 'locale',
  COOKIE_CONSENT = 'cookie_consent',
  CHATBOT_SESSION = 'chatbot_session',
}

/**
 * Metadata для кэша
 */
interface CacheMetadata {
  timestamp: number
  version: string
}

interface CachedData<T> {
  data: T
  metadata: CacheMetadata
}

class LocalStorageService {
  private readonly APP_VERSION = '1.0.0'
  private readonly DEFAULT_TTL = 24 * 60 * 60 * 1000 // 24 часа

  /**
   * Получить ключ с префиксом
   */
  private getKey(key: StorageKey): string {
    return `${STORAGE_PREFIX}${key}`
  }

  /**
   * Сохранить данные в localStorage
   */
  set<T>(key: StorageKey, value: T): void {
    try {
      const cachedData: CachedData<T> = {
        data: value,
        metadata: {
          timestamp: Date.now(),
          version: this.APP_VERSION,
        },
      }
      const serialized = JSON.stringify(cachedData)
      localStorage.setItem(this.getKey(key), serialized)
    } catch (error) {
      console.error(`Failed to save to localStorage: ${key}`, error)
    }
  }

  /**
   * Получить данные из localStorage
   */
  get<T>(key: StorageKey): T | null {
    try {
      const item = localStorage.getItem(this.getKey(key))
      if (!item) return null

      const cached: CachedData<T> = JSON.parse(item)
      return cached.data
    } catch (error) {
      console.error(`Failed to get from localStorage: ${key}`, error)
      return null
    }
  }

  /**
   * Получить данные с проверкой актуальности (TTL)
   */
  getWithTTL<T>(key: StorageKey, ttl: number = this.DEFAULT_TTL): T | null {
    try {
      const item = localStorage.getItem(this.getKey(key))
      if (!item) return null

      const cached: CachedData<T> = JSON.parse(item)
      
      // Проверка актуальности
      const isExpired = Date.now() - cached.metadata.timestamp > ttl
      if (isExpired) {
        this.remove(key)
        return null
      }

      // Проверка версии
      if (cached.metadata.version !== this.APP_VERSION) {
        this.remove(key)
        return null
      }

      return cached.data
    } catch (error) {
      console.error(`Failed to get from localStorage: ${key}`, error)
      return null
    }
  }

  /**
   * Удалить данные из localStorage
   */
  remove(key: StorageKey): void {
    try {
      localStorage.removeItem(this.getKey(key))
    } catch (error) {
      console.error(`Failed to remove from localStorage: ${key}`, error)
    }
  }

  /**
   * Очистить весь localStorage приложения
   */
  clear(): void {
    try {
      Object.values(StorageKey).forEach((key) => {
        this.remove(key as StorageKey)
      })
    } catch (error) {
      console.error('Failed to clear localStorage', error)
    }
  }

  /**
   * Проверить существует ли ключ
   */
  has(key: StorageKey): boolean {
    return localStorage.getItem(this.getKey(key)) !== null
  }

  /**
   * Получить размер хранилища в байтах
   */
  getSize(): number {
    let size = 0
    try {
      Object.values(StorageKey).forEach((key) => {
        const item = localStorage.getItem(this.getKey(key as StorageKey))
        if (item) {
          size += item.length + (this.getKey(key as StorageKey)).length
        }
      })
    } catch (error) {
      console.error('Failed to calculate storage size', error)
    }
    return size
  }
}

export const localStorageService = new LocalStorageService()
